"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, CheckIcon, SearchIcon } from "lucide-react";
import {
  COUNTRY_CODES,
  CountryCode,
} from "@component/country-codes-data";
import { Input } from "@component/input";

interface CountryCodeSelectProps {
  value: CountryCode;
  disabled?: boolean;
  onChange: (value: CountryCode) => void;
  height?: string;
}

export default function CountryCodeSelect({
  value,
  disabled,
  onChange,
  height,
}: CountryCodeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredCountries = searchQuery
    ? COUNTRY_CODES.filter(
      (country) =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.includes(searchQuery)
    )
    : COUNTRY_CODES;

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery("");
      // Focus the search input when opening
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 10);
    }
  };

  const selectCountry = (country: CountryCode) => {
    onChange(country);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        className={`flex items-center justify-center border border-r-0 rounded-l-md px-3 bg-muted ${height || "h-[36px]"} min-w-[80px] hover:bg-muted/80 transition-colors`}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-sm mr-1">{value.flag}</span>
        <span className="mx-1 text-sm">{value.code}</span>
        <ChevronDownIcon
          size={14}
          className={`text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 left-0 bg-background border rounded-md shadow-lg w-[280px] max-h-[300px] flex flex-col overflow-hidden"
          role="listbox"
        >
          <div className="p-2 border-b sticky top-0 bg-background z-10">
            <div className="relative">
              <SearchIcon
                size={16}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              />

              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search countries..."
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, index) => (
                <button
                  key={`${country.code}-${country.name}-${index}`}
                  type="button"
                  className={`flex items-center w-full px-3 py-2 hover:bg-muted text-left text-sm ${value.code === country.code && value.name === country.name
                    ? "bg-muted/50"
                    : ""
                    }`}
                  onClick={() => selectCountry(country)}
                  role="option"
                  aria-selected={
                    value.code === country.code && value.name === country.name
                  }
                >
                  <span className="mr-2">{country.flag}</span>
                  <span className="text-sm">{country.code}</span>
                  <span className="ml-2 text-muted-foreground truncate">
                    {country.name}
                  </span>
                  {value.code === country.code &&
                    value.name === country.name && (
                      <CheckIcon size={16} className="ml-auto text-primary" />
                    )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
